// frontend/src/pages/crm/Settings.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { authAPI, agencyAPI } from '../../utils/crmApi';
import { useSubscription } from '../../contexts/SubscriptionContext';
import {
    UserIcon,
    ShieldCheckIcon,
    CreditCardIcon,
    BellIcon,
    KeyIcon,
    DevicePhoneMobileIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import ImageUpload from '../../components/ui/image-upload';
import toast from 'react-hot-toast';

const Settings = () => {
    const { user, currentAgency, refreshCurrentAgency, forceRefresh, agencies = [], switchAgency } = useCRM();
    const navigate = useNavigate();
    const { branding } = useBranding();
    const { subscription, planCatalog, SUBSCRIPTION_TIERS } = useSubscription();
    const [activeTab, setActiveTab] = useState('profile');
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState(null);
    const [agency, setAgency] = useState(null);
    
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        phone: '',
        title: '',
        bio: '',
        profileImage: ''
    });

    const [securityData, setSecurityData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        twoFactorEnabled: false
    });

    const [notificationSettings, setNotificationSettings] = useState({
        emailNotifications: true,
        taskReminders: true,
        clientUpdates: true,
        systemAlerts: true,
        marketingEmails: false
    });

    useEffect(() => {
        if (user) {
            loadUserData();
        }
        if (currentAgency?._id) {
            loadAgencyData();
        }
    }, [user, currentAgency?._id]);

    const loadUserData = async () => {
        try {
            // In a real app, you'd fetch user data from API
            setUserData(user);
            setProfileData({
                name: user?.name || '',
                email: user?.email || '',
                phone: user?.phone || '',
                title: user?.title || '',
                bio: user?.bio || '',
                profileImage: user?.profileImage || ''
            });
        } catch (error) {
            console.error('Failed to load user data:', error);
            toast.error('Failed to load user data');
        }
    };

    const loadAgencyData = async () => {
        try {
            const response = await agencyAPI.getById(currentAgency._id);
            if (response.data.success) {
                setAgency(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load agency data:', error);
        }
    };

    const tabs = [
        { id: 'profile', label: 'Profile', icon: UserIcon },
        { id: 'security', label: 'Security', icon: ShieldCheckIcon },
        { id: 'notifications', label: 'Notifications', icon: BellIcon },
        { id: 'billing-access', label: 'My Billing Access', icon: CreditCardIcon }
    ];

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSecurityChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSecurityData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleNotificationChange = (e) => {
        const { name, checked } = e.target;
        setNotificationSettings(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleProfileImageChange = (imageData) => {
        // Support clearing the image when X is clicked (imageData can be null)
        setProfileData(prev => ({
            ...prev,
            profileImage: imageData || ''
        }));
        if (imageData) {
            toast.success('Profile image updated!');
        } else {
            toast.success('Profile image removed. Click Save to persist.');
        }
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setLoading(true);
            const response = await authAPI.updateMe(profileData);
            const data = response.data || {};
            if (data.success) {
                toast.success('Profile updated successfully!');
                // Refresh user/context so header avatar updates immediately
                if (typeof forceRefresh === 'function') {
                    forceRefresh();
                }
            } else {
                toast.success('Profile updated successfully!');
                if (typeof forceRefresh === 'function') {
                    forceRefresh();
                }
            }
        } catch (error) {
            console.error('Failed to update profile:', error);
            toast.error('Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleSecuritySubmit = async (e) => {
        e.preventDefault();
        
        // Validate current password is provided
        if (!securityData.currentPassword) {
            toast.error('Current password is required');
            return;
        }
        
        if (securityData.newPassword !== securityData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (securityData.newPassword.length < 8) {
            toast.error('New password must be at least 8 characters long');
            return;
        }

        try {
            setLoading(true);
            
            await authAPI.changePassword({
                currentPassword: securityData.currentPassword,
                newPassword: securityData.newPassword
            });
            
            toast.success('Password changed successfully!');
            setSecurityData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
                twoFactorEnabled: securityData.twoFactorEnabled
            });
        } catch (error) {
            console.error('Failed to change password:', error);
            const errorMessage = error.response?.data?.message || 'Failed to change password';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setLoading(true);
            
            // In a real app, you'd call userAPI.updateNotificationSettings(notificationSettings)
            toast.success('Notification settings updated!');
        } catch (error) {
            console.error('Failed to update notifications:', error);
            toast.error('Failed to update notification settings');
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-foreground">Profile & Security</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    Manage your personal profile, security settings, and preferences
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-border">
                <nav className="-mb-px flex space-x-8">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`
                                group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                                ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                                }
                            `}
                        >
                            <tab.icon
                                className={`
                                    -ml-0.5 mr-2 h-5 w-5
                                    ${activeTab === tab.id ? 'text-blue-500' : 'text-muted-foreground group-hover:text-foreground'}
                                `}
                            />
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="bg-card rounded-xl shadow-sm border border-border p-6">
                {/* Profile Tab */}
                {activeTab === 'profile' && (
                    <form onSubmit={handleProfileSubmit} className="space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold text-foreground mb-4">Personal Information</h3>
                            
                            {/* Profile Image */}
                            <div className="flex items-center space-x-6 mb-6">
                                <div className="flex-shrink-0">
                                    {profileData.profileImage ? (
                                        <img
                                            src={profileData.profileImage}
                                            alt="Profile"
                                            className="h-20 w-20 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div 
                                            className="h-20 w-20 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                                            style={{ backgroundColor: branding.primaryColor }}
                                        >
                                            {profileData.name?.charAt(0) || 'U'}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <ImageUpload
                                        value={profileData.profileImage}
                                        onChange={handleProfileImageChange}
                                        aspectRatio="1:1"
                                        maxSize={5 * 1024 * 1024} // 5MB
                                        placeholder="Upload profile image"
                                    />
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        JPG, PNG or GIF. Max size 5MB.
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={profileData.name}
                                        onChange={handleProfileChange}
                                        required
                                        className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Email Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={profileData.email}
                                        onChange={handleProfileChange}
                                        required
                                        className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={profileData.phone}
                                        onChange={handleProfileChange}
                                        placeholder="+34 123 456 789"
                                        className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Job Title
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={profileData.title}
                                        onChange={handleProfileChange}
                                        placeholder="e.g., Relocation Specialist"
                                        className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Bio
                                </label>
                                <textarea
                                    name="bio"
                                    value={profileData.bio}
                                    onChange={handleProfileChange}
                                    rows={3}
                                    placeholder="Tell us about yourself..."
                                    className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end pt-4 border-t border-border">
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50"
                            >
                                {loading ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                    <div className="space-y-6">
                        {/* Password Change */}
                        <form onSubmit={handleSecuritySubmit} className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-4">Change Password</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1">
                                            Current Password <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="password"
                                            name="currentPassword"
                                            value={securityData.currentPassword}
                                            onChange={handleSecurityChange}
                                            required
                                            className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                New Password <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                name="newPassword"
                                                value={securityData.newPassword}
                                                onChange={handleSecurityChange}
                                                required
                                                minLength={8}
                                                className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1">
                                                Confirm New Password <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="password"
                                                name="confirmPassword"
                                                value={securityData.confirmPassword}
                                                onChange={handleSecurityChange}
                                                required
                                                minLength={8}
                                                className="w-full px-4 py-2 border border-input rounded-lg b bg-background text-foregroundg-background text-foreground focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end pt-4 border-t border-border">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50"
                                >
                                    {loading ? 'Updating...' : 'Update Password'}
                                </button>
                            </div>
                        </form>

                        {/* Two-Factor Authentication */}
                        <div className="border-t border-border pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-foreground">Two-Factor Authentication</h3>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Add an extra layer of security to your account
                                    </p>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <span className={`text-sm font-medium ${securityData.twoFactorEnabled ? 'text-green-600' : 'text-muted-foreground'}`}>
                                        {securityData.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                    <button
                                        type="button"
                                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            securityData.twoFactorEnabled
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-muted text-foreground'
                                        }`}
                                    >
                                        {securityData.twoFactorEnabled ? 'ON' : 'OFF'}
                                    </button>
                                </div>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                                Two-factor authentication setup coming soon
                            </p>
                        </div>

                        {/* Active Sessions */}
                        <div className="border-t border-border pt-6">
                            <h3 className="text-lg font-semibold text-foreground mb-4">Active Sessions</h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <DevicePhoneMobileIcon className="h-5 w-5 text-muted-foreground" />
                                        <div>
                                            <p className="text-sm font-medium text-foreground">Current Session</p>
                                            <p className="text-xs text-muted-foreground">Chrome on Windows • Madrid, Spain</p>
                                        </div>
                                    </div>
                                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Notifications Tab */}
                {activeTab === 'notifications' && (
                    <form onSubmit={handleNotificationSubmit} className="space-y-6">
                        <div>
                            <h3 className="text-lg font-semibold text-foreground mb-4">Notification Preferences</h3>
                            <div className="space-y-4">
                                {Object.entries(notificationSettings).map(([key, value]) => (
                                    <div key={key} className="flex items-center justify-between">
                                        <div>
                                            <h4 className="text-sm font-medium text-foreground">
                                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </h4>
                                            <p className="text-xs text-muted-foreground">
                                                {key === 'emailNotifications' && 'Receive email notifications for important updates'}
                                                {key === 'taskReminders' && 'Get reminded about upcoming task deadlines'}
                                                {key === 'clientUpdates' && 'Be notified when clients update their information'}
                                                {key === 'systemAlerts' && 'Receive system alerts and maintenance notifications'}
                                                {key === 'marketingEmails' && 'Receive promotional emails and product updates'}
                                            </p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name={key}
                                                checked={value}
                                                onChange={handleNotificationChange}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-ring rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-input after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end pt-4 border-t border-border">
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50"
                            >
                                {loading ? 'Saving...' : 'Save Preferences'}
                            </button>
                        </div>
                    </form>
                )}

                {/* My Billing Access Tab */}
                {activeTab === 'billing-access' && (
                    <div className="space-y-6">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-sm text-yellow-800">
                                Billing is managed per agency. Use the links below to open the Billing page for agencies you can manage.
                                        </p>
                                    </div>
                        <div className="space-y-3">
                            {agencies.map((a) => {
                                const mem = (user?.agencyMemberships || []).find(m => String(m.agencyId?._id || m.agencyId) === String(a._id));
                                const canManage = user?.isSuperadmin || (mem && (mem.role === 'agency_admin'));
                                return (
                                    <div key={a._id} className="flex items-center justify-between border border-border rounded-lg p-4">
                                        <div>
                                            <div className="text-sm font-medium text-foreground">{a.name}</div>
                                            <div className="text-xs text-muted-foreground">Role: {mem?.role || 'member'}</div>
                                        </div>
                                        <div>
                                            <button
                                                disabled={!canManage}
                                                onClick={async () => {
                                                    try {
                                                        if (typeof switchAgency === 'function') {
                                                            await switchAgency(a._id);
                                                        }
                                                        navigate(`/agency/${a._id}/billing`);
                                                    } catch (e) {}
                                                }}
                                                className={`px-3 py-2 text-sm rounded ${canManage ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground'}`}
                                            >
                                                {canManage ? 'Open Billing' : 'No Billing Access'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Settings;